import {
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Popper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../../api/api';
import { ExoAutoCompleteSelector } from '../../exo/ExoAutoCompleteSelector';
import ExoAvatar from '../../exo/ExoAvatar';
import { createElement, useEffect, useState } from 'react';
import { SettingsPopover } from '../../exo/project/SettingsPopover';
import PersonRemoveAlt1Icon from '@mui/icons-material/PersonRemoveAlt1';
import EmailIcon from '@mui/icons-material/Email';
import { updateOrCreate } from '../../special/updateOrCreate';
import { searchResultMap } from '../../exo/search/searchResultsMap';
import { FormFieldsHighlightContainer } from '../FormFieldsHighlightContainer';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExoDialog from '../../exo/ExoDialog';
import { ExoSmartParticipants } from '../../exo/ExoSmartParticipants';

export const TypeParticipantsAutoComplete = ({
  updateValidationOnChange,
  dataCollection,
  field,
  errors,
  size,
  validationSchema,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [groupedParticipants, setGroupedParticipants] = useState({});

  function handleChange(data, type) {
    const typeMap = {
      company: 'companies',
      contact: 'contacts',
      personLead: 'personLeads',
      companyLead: 'companyLeads',
      user: 'users',
    };

    const realType = typeMap[type] || 'customs';

    const newGroupedParticipants = groupedParticipants;
    if (!newGroupedParticipants[realType])
      newGroupedParticipants[realType] = [];

    // prepair entry
    const selectedEmail = getParticipantEmail(data, realType);
    const newEntry = {
      ...data,
      selectedEmail,
      hasNoMail: !selectedEmail,
    };
    newGroupedParticipants[realType] = updateOrCreate(
      newGroupedParticipants[realType],
      newEntry,
      false,
      realType === 'customs' ? 'selectedEmail' : 'id'
    );

    setGroupedParticipants(newGroupedParticipants);
    updateValidationOnChange(
      [field.key],
      convertIntoParticipantsArray(newGroupedParticipants)
    );
  }

  function convertIntoParticipantsArray(newGroupedParticipants) {
    const typeMap = {
      companies: 'company',
      contacts: 'contact',
      personLeads: 'personLead',
      companyLeads: 'companyLead',
      users: 'user',
      customs: undefined,
    };

    var newPartArray = [];
    // normal
    participantTypeArray.forEach((element) => {
      const arr = newGroupedParticipants[element.key];

      if (!arr) return;
      arr.forEach((participant) => {
        newPartArray.push({
          email: participant.selectedEmail,
          type: typeMap[element.key],
          participant,
        });
      });
    });

    return newPartArray;
  }

  function removeElement(type, elementToRemove) {
    if (!type) type = 'customs';
    const updatedList = (groupedParticipants[type] || []).filter(
      (obj) => obj.id !== elementToRemove.id
    );
    const updatedGroupedParticipants = {
      ...groupedParticipants,
      [type]: updatedList,
    };
    updateValidationOnChange(
      [field.key],
      convertIntoParticipantsArray(updatedGroupedParticipants)
    );
    setGroupedParticipants(updatedGroupedParticipants);
  }

  function handleMailSelect(type, elementToUpdate) {
    const updatedPartList = updateOrCreate(
      groupedParticipants[type],
      elementToUpdate
    );
    const newGroupedParticipants = {
      ...groupedParticipants,
      [type]: updatedPartList,
    };
    setGroupedParticipants(newGroupedParticipants);
    updateValidationOnChange(
      [field.key],
      convertIntoParticipantsArray(newGroupedParticipants)
    );
  }

  // start data conversion
  function convertIntoGroupedParticipants(newParticipantsArray) {
    if (!newParticipantsArray) return {};
    const typeMap = {
      company: 'companies',
      contact: 'contacts',
      personLead: 'personLeads',
      companyLead: 'companyLeads',
      user: 'users',
    };

    var newGroupedParticipants = {};
    newParticipantsArray.forEach((element) => {
      const realType = typeMap[element.type] || 'customs';

      if (!newGroupedParticipants[realType])
        newGroupedParticipants[realType] = [];

      const convertedParticipant = {
        ...element.participant,
        selectedEmail: element.email,
        hasNoMail: !element.email,
      };

      newGroupedParticipants[realType].push(convertedParticipant);
    });

    return newGroupedParticipants;
  }

  useEffect(() => {
    if (dataCollection[field.key])
      setGroupedParticipants(
        convertIntoGroupedParticipants(dataCollection[field.key])
      );
  }, [dataCollection[field.key]]);

  // suggestions for auto fill
  const [suggestions, setSuggestions] = useState([]);
  const [anchor, setAnchor] = useState(null);
  const handleClick = (event) => {
    setAnchor(anchor ? null : event.currentTarget);
  };

  useEffect(() => {
    var newSuggestions = [];
    if (!Boolean(field.suggestionsArr)) return setSuggestions(newSuggestions);

    field.suggestionsArr.forEach((sugg) => {
      const rawData = dataCollection[sugg.key];
      if (!rawData) return;

      newSuggestions = convertIntoSuggestion(rawData, sugg);
    });

    setSuggestions(newSuggestions);
  }, [dataCollection, config]);

  function convertIntoSuggestion(element, sugg) {
    var newSuggArr = [];

    element.persons?.forEach((element) => {
      newSuggArr.push({
        email: element.emails[0]?.emailAddress,
        type: 'personLead',
        participant: element,
        label: convertElementIntoLabel(element, 'personLead'),
      });
    });
    element.managingPersons?.forEach((element) => {
      newSuggArr.push({
        email:
          element.emails.length > 0 ? element.emails[0]?.emailAddress : null,
        type: 'personLead',
        participant: element,
        label: convertElementIntoLabel(element, 'personLead'),
      });
    });
    element.companies?.forEach((element) => {
      newSuggArr.push({
        email: element.emails[0]?.emailAddress,
        type: 'companyLead',
        participant: element,
        label: convertElementIntoLabel(element, 'companyLead'),
      });
    });

    return newSuggArr;
  }

  function convertElementIntoLabel(element, type) {
    if (!element) return {};
    const typeLabelMap = {
      company: (e) => e.nameShort || e.name,
      companyLead: (e) => e.nameShort || e.name,
      contact: (e) => `${e.salutation} ${e.firstName} ${e.lastName}`,
      personLead: (e) => `${e.salutation} ${e.firstName} ${e.lastName}`,
      user: (e) => `${e.salutation} ${e.firstName} ${e.lastName}`,
    };
    if (!typeLabelMap[type])
      console.warn('No Address Element Label Type exists with the type', type);
    return typeLabelMap[type](element);
  }

  function handleSuggClick(sugg) {
    const newArr = removeDuplicates([...dataCollection[field.key], sugg]);
    updateValidationOnChange([field.key], newArr);
    setAnchor(null);
  }

  function removeDuplicates(arr) {
    const seen = new Set();
    return arr.filter((item) => {
      const key = `${item.type}-${item.participant.id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  function handleAdd(email) {
    handleChange({ email });
  }

  // smart select

  const [openSmartSelect, setOpenSmartSelect] = useState(false);
  function handleSelectedParticipants(selectedParticipants) {
    const newParticipantsArray = [
      ...(dataCollection[field.key] || []),
      ...selectedParticipants,
    ];

    updateValidationOnChange([field.key], newParticipantsArray);
    setOpenSmartSelect(false);
  }

  return (
    <Box className="flex flex-col w-full rounded-xl">
      <Box className="flex gap-2">
        <FormFieldsHighlightContainer
          error={errors[field.key]}
          field={field}
          hasValidation={Boolean(validationSchema?.fields[field.key])}
        >
          <ExoAutoCompleteSelector
            multiselect={field.multiselect}
            selection={dataCollection[field.key]}
            onSelectionChange={handleChange}
            searchParams={
              field.searchParams || [
                'companies',
                'contacts',
                'person_leads',
                'company_leads',
                'users',
              ]
            }
            onAdd={handleAdd}
            label={field.label}
            size={size}
          />
        </FormFieldsHighlightContainer>
        {suggestions.length > 0 && (
          <>
            <Badge badgeContent={suggestions.length} color="info" size="small">
              <Button
                sx={{
                  bgcolor: colors.bgInput,
                  borderRadius: '10px',
                  minWidth: size === 'small' ? '40px' : '53px',
                }}
                onClick={handleClick}
              >
                <Diversity3Icon />
              </Button>
            </Badge>
            <Popper
              anchorEl={anchor}
              open={Boolean(anchor)}
              sx={{ zIndex: 9999 }}
              className="flex justify-center flex-col items-end"
              placement="bottom-end"
            >
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderBottom: `5px solid ${colors.selected}`,
                  marginRight: size === 'small' ? '15px' : '25px',
                }}
              />
              <ClickAwayListener onClickAway={() => setAnchor(null)}>
                <Box
                  className="rounded-lg px-2 py-1 shadow-md flex flex-col gap-1 backdrop-blur-md"
                  sx={{
                    background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
                    border: `1px solid ${colors.selected}`,
                  }}
                >
                  <Typography variant="h6">
                    {t('Quick participant selection')}
                  </Typography>
                  {suggestions.map((sugg) => (
                    <Button
                      onClick={() => handleSuggClick(sugg)}
                      sx={{
                        justifyContent: 'start',
                        alignItems: 'start',
                        border: `1px solid transparent`,
                        ':hover': {
                          bgcolor: colors.info + '30',
                          border: `1px solid ${colors.info}`,
                        },
                      }}
                      className="flex-col"
                    >
                      <Typography fontWeight={500}>{sugg.label}</Typography>
                      <Typography fontSize={12} className="opacity-70">
                        {sugg.email}
                      </Typography>
                    </Button>
                  ))}
                </Box>
              </ClickAwayListener>
            </Popper>
          </>
        )}
        {field.allowSmartSelect && (
          <>
            <Button
              sx={{
                bgcolor: colors.bgInput,
                borderRadius: '10px',
                minWidth: size === 'small' ? '40px' : '53px',
              }}
              onClick={() => setOpenSmartSelect(true)}
            >
              <AutoAwesomeIcon />
            </Button>
            <ExoDialog
              open={openSmartSelect}
              onClose={() => setOpenSmartSelect(false)}
            >
              <ExoSmartParticipants
                onClose={() => setOpenSmartSelect(false)}
                onSelect={handleSelectedParticipants}
              />
            </ExoDialog>
          </>
        )}
      </Box>
      <Box
        className="px-2 rounded-b-xl mx-3"
        sx={{
          border: `${
            !dataCollection[field.key] || dataCollection[field.key]?.length < 1
              ? 0
              : 2
          }px solid ${colors.bgInput}`,
          borderTopWidth: '0px',
        }}
      >
        {participantTypeArray.map(
          (type) =>
            groupedParticipants &&
            groupedParticipants[type.key] &&
            groupedParticipants[type.key]?.length > 0 && (
              <Box className="pt-1 pb-1" key={type.key}>
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  className="opacity-60"
                >
                  {t(type.label)}
                </Typography>
                {groupedParticipants[type.key].map((element) =>
                  createElement(type.Component, {
                    key: `${type.key}-${element.id}`,
                    data: element,
                    onRemove: () => removeElement(type.key, element),
                    groupedParticipants,
                    hideEmail: field.hideEmail,
                    onMailSelect: (updatedElement) =>
                      handleMailSelect(type.key, updatedElement),
                  })
                )}
              </Box>
            )
        )}
      </Box>
    </Box>
  );
};

const Employee = ({ data, status, onRemove, noAction, hideEmail }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();
  const { picture, salutation, firstName, lastName, email, position } = data;

  const name = `${firstName} ${lastName}`;

  return (
    <Box
      className="flex items-center gap-2 rounded-md pl-1 justify-between"
      sx={{
        border: `1px solid ${status ? status.color : 'transparent'}`,
        bgcolor: status ? `${status.color}${status.bgTrans}` : 'transparent',
      }}
    >
      <Box className="flex items-center gap-2">
        {Boolean(status) && (
          <Tooltip title={status.label}>
            {createElement(status.Icon, {
              sx: { color: status.color },
            })}
          </Tooltip>
        )}
        <ExoAvatar picture={picture?.temporaryUrl} name={name} size="30px" />
        <Box className="flex flex-col">
          <Typography
            fontWeight={500}
          >{`${salutation} ${firstName} ${lastName}`}</Typography>
          {!hideEmail && (
            <Typography fontSize={12} className="opacity-50">
              {email}
            </Typography>
          )}
        </Box>
      </Box>
      {!noAction && (
        <SettingsPopover
          label={t('Actions')}
          settings={[
            {
              onClick: () => onRemove(data),
              label: t('Remove'),
              color: 'error',
              Icon: PersonRemoveAlt1Icon,
            },
          ]}
        />
      )}
    </Box>
  );
};

const Custom = ({ data, status, onRemove, noAction, hideEmail }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();
  const { email, selectedEmail } = data;

  return (
    <Box
      className="flex items-center gap-2 rounded-md pl-1 justify-between"
      sx={{
        border: `1px solid ${status ? status.color : 'transparent'}`,
        bgcolor: status ? `${status.color}${status.bgTrans}` : 'transparent',
      }}
    >
      <Box className="flex items-center gap-2">
        {Boolean(status) && (
          <Tooltip title={status.label}>
            {createElement(status.Icon, {
              sx: { color: status.color },
            })}
          </Tooltip>
        )}
        <ExoAvatar icon={<AddIcon />} size="30px" />
        <Box className="flex flex-col">
          {!hideEmail && (
            <Typography fontWeight={500}>{selectedEmail}</Typography>
          )}
        </Box>
      </Box>
      {!noAction && (
        <SettingsPopover
          label={t('Actions')}
          settings={[
            {
              onClick: () => onRemove(data),
              label: t('Remove'),
              color: 'error',
              Icon: PersonRemoveAlt1Icon,
            },
          ]}
        />
      )}
    </Box>
  );
};

const Contact = ({
  data,
  onMailSelect,
  status,
  onRemove,
  groupedParticipants,
  hideEmail,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const {
    salutation,
    firstName,
    lastName,
    emails,
    color,
    selectedEmail,
    hasNoMail,
  } = data;
  const { t } = useTranslation();

  const [mailSettings, setMailSettings] = useState(null);

  useEffect(() => {
    const newSettings = [];

    emails?.forEach((element) => {
      newSettings.push({
        onClick: () =>
          onMailSelect({
            ...data,
            selectedEmail: element.emailAddress,
          }),
        label: `${element.emailType} <${element.emailAddress}>`,
        Icon: EmailIcon,
      });
    });

    // add remove button
    newSettings.push({
      onClick: () => onRemove(data),
      label: t('Remove'),
      color: 'error',
      Icon: PersonRemoveAlt1Icon,
    });

    setMailSettings(newSettings);
  }, [groupedParticipants]);

  const name = `${firstName} ${lastName}`;

  return (
    <Box
      className="flex items-center gap-2 rounded-md pl-1"
      sx={{
        border: `1px solid ${status ? status.color : 'transparent'}`,
        bgcolor: status ? `${status.color}${status.bgTrans}` : 'transparent',
      }}
    >
      {Boolean(status) && (
        <Tooltip title={status.label}>
          {createElement(status.Icon, { sx: { color: status.color } })}
        </Tooltip>
      )}
      <ExoAvatar size="30px" name={name} color={color} />
      <Box className="flex flex-col w-full justify-between">
        <Typography
          fontWeight={500}
        >{`${salutation} ${firstName} ${lastName}`}</Typography>
        {!hideEmail && (
          <Typography
            fontSize={12}
            className="opacity-60"
            color={hasNoMail ? 'error' : ''}
          >
            {hasNoMail ? t('No email available') : selectedEmail}
          </Typography>
        )}
      </Box>
      {mailSettings && (
        <SettingsPopover label={t('E-Mails')} settings={mailSettings} />
      )}
    </Box>
  );
};
const Company = ({
  data,
  onMailSelect,
  status,
  onRemove,
  groupedParticipants,
  hideEmail,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { logo, name, emails, color, selectedEmail, nameShort, hasNoMail } =
    data;
  const { t } = useTranslation();

  const [mailSettings, setMailSettings] = useState(null);

  useEffect(() => {
    const newSettings = [];

    emails?.forEach((element) => {
      newSettings.push({
        onClick: () =>
          onMailSelect({
            ...data,
            selectedEmail: element.emailAddress,
          }),
        label: `${element.emailType} <${element.emailAddress}>`,
      });
    });

    // add remove button
    newSettings.push({
      onClick: () => onRemove(data),
      label: t('Remove'),
      color: 'error',
      Icon: PersonRemoveAlt1Icon,
    });

    setMailSettings(newSettings);
  }, [groupedParticipants]);

  return (
    <Box
      className="flex items-center gap-2 rounded-md pl-1"
      sx={{
        border: `1px solid ${status ? status.color : 'transparent'}`,
        bgcolor: status ? `${status.color}${status.bgTrans}` : 'transparent',
      }}
    >
      {Boolean(status) && (
        <Tooltip title={status.label}>
          {createElement(status.Icon, { sx: { color: status.color } })}
        </Tooltip>
      )}
      <ExoAvatar
        type="company"
        size="30px"
        picture={logo?.thumbnailUrl}
        name={nameShort || name}
        color={color}
      />
      <Box className="flex flex-col w-full justify-between">
        <Typography fontWeight={500}>{nameShort || name}</Typography>
        {!hideEmail && (
          <Typography
            fontSize={12}
            className="opacity-50"
            color={hasNoMail ? 'error' : ''}
          >
            {hasNoMail ? t('No email available') : selectedEmail}
          </Typography>
        )}
      </Box>
      {mailSettings && (
        <SettingsPopover label={t('E-Mails')} settings={mailSettings} />
      )}
    </Box>
  );
};

export const participantTypeArray = [
  {
    label: 'Company Leads',
    key: 'companyLeads',
    Icon: searchResultMap['company_leads'].Icon,
    Component: Company,
    emailKey: 'emails',
    emailType: 'array',
  },
  {
    label: 'Person Leads',
    key: 'personLeads',
    Icon: searchResultMap['person_leads'].Icon,
    Component: Contact,
    emailKey: 'emails',
    emailType: 'array',
  },
  {
    label: 'Companies',
    key: 'companies',
    Icon: searchResultMap['companies'].Icon,
    Component: Company,
    emailKey: 'emails',
    emailType: 'array',
  },
  {
    label: 'Contacts',
    key: 'contacts',
    Icon: searchResultMap['contacts'].Icon,
    Component: Contact,
    emailKey: 'emails',
    emailType: 'array',
  },
  {
    label: 'Employee',
    key: 'users',
    Icon: searchResultMap['users'].Icon,
    Component: Employee,
    emailKey: 'email',
    emailType: 'value',
  },
  {
    label: 'Custom',
    key: 'customs',
    Icon: AddIcon,
    Component: Custom,
    emailKey: 'email',
    emailType: 'value',
  },
];

export function getParticipantEmail(participant, type) {
  if (type === 'customs') return participant.email;
  const typeObj = participantTypeArray.find((obj) => obj.key === type);

  // get email
  var selectedEmail = participant.selectedEmail;
  if (!selectedEmail && typeObj.emailType === 'array') {
    participant[typeObj.emailKey].forEach((email) => {
      if (email.isPrimary) selectedEmail = email.emailAddress;
    });
  }
  if (!selectedEmail && typeObj.emailType === 'value') {
    selectedEmail = participant[typeObj.emailKey];
  }

  return selectedEmail;
}
